import { makeRequest } from "../../../apis/api";
import { FetchFeeByType } from "../../../apis/Fee";
import {
  BulkCreateListings,
  FetchBulkCreateOptions,
  ValidateBulkCreateListings,
} from "../../../apis/Property";
import { SearchRecords } from "../../../apis/User";
import {
  ALLOWED_PHOTO_TYPES,
  MAX_PHOTO_BYTES,
  MAX_PHOTO_DIMENSION,
} from "./constants";
import { getPhoneDigits } from "./validation";

export const loadBulkCatalogs = async () => {
  const response = await FetchBulkCreateOptions();
  if (!response.success || !response.catalogs) {
    throw new Error("Could not load enabled listing options.");
  }
  return response.catalogs;
};

export const loadVatPercent = async () => {
  try {
    const response = await FetchFeeByType("vat");
    const fees = response?.data?.fees || [];
    const value = fees[0]?.value;
    const n = Number(value);
    return Number.isFinite(n) ? n : 15;
  } catch {
    return 15;
  }
};

export const searchCities = async (query = "") => {
  const result = await makeRequest(
    `/api/search/home/cities?search=${encodeURIComponent(query)}`,
  );
  const hits = Array.isArray(result)
    ? result
    : result?.hits || result?.data || [];
  return (hits || [])
    .map((hit) => ({
      cityId: hit.cityId,
      en: hit.nameEn || "",
      ar: hit.nameAr || "",
    }))
    .filter((c) => c.en || c.ar)
    .sort((a, b) => a.en.localeCompare(b.en));
};

const USER_LOOKUP_PAGE_SIZE = "100";
const USER_LOOKUP_CACHE_MS = 60_000;

let usersCache = { at: 0, users: null, promise: null };

const userDisplayName = (user) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || "unnamed";
};

/** Normalize SA mobiles to a 9-digit local form when possible (5xxxxxxxx). */
const toLocalPhone = (digits) => {
  let value = getPhoneDigits(digits);
  if (!value) return "";
  if (value.startsWith("00966")) value = value.slice(5);
  else if (value.startsWith("966")) value = value.slice(3);
  if (value.length === 10 && value.startsWith("0")) value = value.slice(1);
  return value;
};

/**
 * Same idea as CreateMultiUnitListing: SearchRecords(key) does not reliably
 * match phone numbers, so we load users and compare digits client-side.
 */
const phoneMatches = (userPhone, searchDigits) => {
  const phone = getPhoneDigits(userPhone);
  const search = getPhoneDigits(searchDigits);
  if (!phone || !search || search.length < 9) return false;

  const phoneLocal = toLocalPhone(phone);
  const searchLocal = toLocalPhone(search);

  return (
    phone === search ||
    phoneLocal === searchLocal ||
    phone.includes(search) ||
    search.includes(phone) ||
    phoneLocal.includes(searchLocal) ||
    searchLocal.includes(phoneLocal)
  );
};

const fetchAllUsersForPhoneLookup = async () => {
  const now = Date.now();
  if (usersCache.users && now - usersCache.at < USER_LOOKUP_CACHE_MS) {
    return usersCache.users;
  }
  if (usersCache.promise) return usersCache.promise;

  usersCache.promise = (async () => {
    const firstPageResponse = await SearchRecords(
      "",
      "1",
      USER_LOOKUP_PAGE_SIZE,
      "all",
    );
    if (!firstPageResponse.success) {
      throw new Error("Could not look up account.");
    }

    const firstPageData = firstPageResponse.data;
    const totalPages = Math.max(1, Number(firstPageData?.totalPages) || 1);
    const otherPageResponses = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        SearchRecords("", String(index + 2), USER_LOOKUP_PAGE_SIZE, "all"),
      ),
    );

    if (otherPageResponses.some((page) => !page.success)) {
      throw new Error("Could not look up account.");
    }

    const users = [
      ...(firstPageData?.results || []),
      ...otherPageResponses.flatMap((page) => page.data?.results || []),
    ];
    usersCache = { at: Date.now(), users, promise: null };
    return users;
  })().catch((error) => {
    usersCache.promise = null;
    throw error;
  });

  return usersCache.promise;
};

export const lookupAccountByMobile = async (mobileNumber) => {
  const digits = getPhoneDigits(mobileNumber);
  if (digits.length < 9) {
    return { status: "short", users: [], message: "Type the client mobile" };
  }

  const allUsers = await fetchAllUsersForPhoneLookup();
  const matches = allUsers.filter((user) =>
    phoneMatches(user.mobileNumber, digits),
  );

  if (!matches.length) {
    return {
      status: "none",
      users: [],
      message: "No account uses this number",
    };
  }

  if (matches.length > 1) {
    return {
      status: "multiple",
      users: matches,
      message: `Multiple accounts — pick an Account ID: ${matches
        .map(
          (u) =>
            `${u._id} (${userDisplayName(u)}, ${u.role?.name || "unknown"})`,
        )
        .join("; ")}`,
    };
  }

  const user = matches[0];
  return {
    status: "found",
    users: [user],
    userId: String(user._id),
    message: `Account: ${userDisplayName(user)} · ${user.role?.name || "user"} · ${user.mobileNumber}`,
  };
};

export const validateListingsOnServer = (rows) =>
  ValidateBulkCreateListings(rows);

export const submitBulkListings = (rows, photosByRowNumber) => {
  const filesOnly = {};
  Object.entries(photosByRowNumber || {}).forEach(([rowNumber, entries]) => {
    filesOnly[rowNumber] = (entries || []).map((entry) => entry.file);
  });
  return BulkCreateListings(rows, filesOnly);
};

export const readImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const dimensions = {
        width: image.naturalWidth,
        height: image.naturalHeight,
      };
      URL.revokeObjectURL(objectUrl);
      resolve(dimensions);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Could not read image dimensions for ${file.name}`));
    };
    image.src = objectUrl;
  });

export const validatePhotoFile = async (file) => {
  if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
    throw new Error(`${file.name}: use JPG, PNG, or WebP.`);
  }
  if (file.size > MAX_PHOTO_BYTES) {
    throw new Error(`${file.name}: each photo must be 5 MB or smaller.`);
  }
  const { width, height } = await readImageDimensions(file);
  if (width > MAX_PHOTO_DIMENSION || height > MAX_PHOTO_DIMENSION) {
    throw new Error(
      `${file.name}: max resolution is ${MAX_PHOTO_DIMENSION}×${MAX_PHOTO_DIMENSION}.`,
    );
  }
  return true;
};

export const revokePhotoUrls = (photosByRow) => {
  Object.values(photosByRow || {}).forEach((entries) => {
    (entries || []).forEach((entry) => {
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    });
  });
};
